import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useEffect } from 'react'
import CreateMemberModal from '../CreateMemberModal'
import { deleteMember, listOctaviaPoolMembers, listOctaviaPools } from '../actions'
import { ILoadBalancerDetailsPageTabs } from '../model'
import { octaviaPoolMembersSelector, octaviaPoolsSelector } from '../selectors'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import CustomDeleteDialog from 'core/components/CustomDeleteDialog'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.OctaviaPoolMembers
type SelectorModel = ArrayElement<ReturnType<typeof octaviaPoolMembersSelector>>
type ActionParams = InferActionParams<typeof listOctaviaPoolMembers>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  poolIds: [],
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const Phase = {
  Pending: 'Pending',
  Active: 'Active',
  Error: 'Error',
  Deleting: 'Deleting',
  Disabled: 'Disabled',
  Unknown: 'Unknown',
  Online: 'Online',
  Offline: 'Offline',
  Draining: 'Draining',
  Degraded: 'Degraded',
  NoMonitor: 'No_Monitor',
} as const

const phaseStatusMap: Record<string, StatusCellModel> = {
  [Phase.Active.toLowerCase()]: { variant: 'success', label: Phase.Active },
  [Phase.Pending.toLowerCase()]: { variant: 'warning', label: Phase.Pending },
  [Phase.Error.toLowerCase()]: { variant: 'error', label: Phase.Error },
  [Phase.Deleting.toLowerCase()]: { variant: 'error', label: Phase.Deleting },
  [Phase.Disabled.toLowerCase()]: { variant: 'warning', label: Phase.Disabled },
  [Phase.Online.toLowerCase()]: { variant: 'success', label: Phase.Online },
  [Phase.Offline.toLowerCase()]: { variant: 'error', label: Phase.Offline },
  [Phase.Draining.toLowerCase()]: { variant: 'warning', label: Phase.Draining },
  [Phase.Degraded.toLowerCase()]: { variant: 'error', label: Phase.Degraded },
  [Phase.NoMonitor.toLowerCase()]: { variant: 'warning', label: 'No Monitor' },
}

const getPhaseStatus = (phase: string): StatusCellModel =>
  phaseStatusMap[phase.trim().toLowerCase()] || { variant: 'unknown', label: Phase.Unknown }

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OctaviaPoolMembers',
  listTablePrefs,
)

const searchTargets = ['name', 'ipAddress', 'operatingStatus', 'provisioningStatus', 'adminStateUp']

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const loadBalancerColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    // width: 'medium',
  },
  {
    key: 'status',
    label: 'Provisioning Status',
    CellComponent: createGridStatusCell({
      dataFn: getPhaseStatus,
    }),
  },
  {
    key: 'operatingStatus',
    label: 'Operating Status',
    CellComponent: createGridStatusCell({
      dataFn: getPhaseStatus,
    }),
  },
  {
    key: 'pool',
    label: 'Pool',
  },
  {
    key: 'ipAddress',
    label: 'IP Address',
  },

  {
    key: 'port',
    label: 'Port',
  },
  {
    key: 'weight',
    label: 'Weight',
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function MembersListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { prefs } = useScopedPreferences()
  const { reload: reloadOctaviaPools } = useListAction(listOctaviaPools)
  const octaviaPools = useAppSelector(octaviaPoolsSelector)
  const poolIds = octaviaPools.map((pool) => pool.id)
  const { message, loading, reload } = useListAction(listOctaviaPoolMembers, {
    params: { poolIds: poolIds },
    requiredParams: ['poolIds'],
  })
  const data = useAppSelector(octaviaPoolMembersSelector)

  const handleRefresh = useCallback(() => {
    reloadOctaviaPools(true)
    reload(true)
  }, [poolIds])

  const DeleteMemberDialog = useCallback(
    (props) => (
      <CustomDeleteDialog
        {...props}
        deleteAction={deleteMember}
        title="Delete Member(s)"
        resourceLabel="Members"
        getRowId={(row) => row?.id}
        getRowName={(row) => row?.name}
        buildBodyFn={(row) => ({ poolId: row?.poolId })}
      />
    ),
    [],
  )

  useEffect(() => {
    reload(true)
  }, [octaviaPools])
  return (
    <>
      <CreateMemberModal addRoute={routeManager.routes.openstack.createLoadBalancers} />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OctaviaPoolMembers}
        label="Members"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={data}
        columns={loadBalancerColumns}
        addUrl={routeManager.routes.openstack.createLoadBalancers.path({
          tab: ILoadBalancerDetailsPageTabs.Members,
        })}
        addText="Add Member"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteMember}
        DeleteDialogComponent={DeleteMemberDialog}
        multiSelection
        emptyContent={
          <EmptyContent title={'No members'} subTitle={'Create one in order to see details'} />
        }
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
